import { Handle, Node as Node_, NodeProps as NodeProps_, Position, useReactFlow } from '@xyflow/react';
import { memo, ReactNode, useEffect } from 'react';

import { merge } from '@/components';
import { Flex } from '@/components/core';

export type ResourceGroupType = 'trigger' | 'model' | 'validation' | 'export' | 'other';

export type NodeProps = {
  children?: () => ReactNode;
  resourceGroupType: ResourceGroupType;
  resourceIds: string[];
  variant?: 'default' | 'emptyState';
};

export type NodeType = Node_<NodeProps, 'node'>;

export const INode = ({ id, data: { children, variant, resourceGroupType }, selected }: NodeProps_<NodeType>) => {
  const reactFlow = useReactFlow();

  const isTrigger = resourceGroupType === 'trigger';
  const isExport = resourceGroupType === 'export';
  const isEmptyState = variant === 'emptyState';

  /* prettier-ignore */
  const styles = [
    'bg-white',
    'border-1',
    'border-gray-300',
    'rounded-xl',
    'shadow-md',
    'w-[20rem]',
    'p-md',
    isEmptyState && 'border-dashed border-primary-500',
    !isEmptyState && selected && 'ring-primary-500 ring-1 ',
  ];

  /* prettier-ignore */
  const handleStyles = [
    'size-3',
    'rounded-full',
    'border-1',
    'border-gray-300'
  ];

  useEffect(() => {
    if (selected) {
      reactFlow.fitView({ maxZoom: 1, duration: 400, nodes: [{ id }] }).catch(console.error);
    }
  }, [id, reactFlow, selected]);

  return (
    <Flex className={merge(styles)}>
      {children?.()}
      {!isTrigger && (
        <Handle isConnectable={false} type="target" position={Position.Top} className={merge(handleStyles)} />
      )}
      {!isExport && (
        <Handle isConnectable={false} type="source" position={Position.Bottom} className={merge(handleStyles)} />
      )}
    </Flex>
  );
};

export const Node = memo(INode) as typeof INode;
